/**
 * Recipe Generation Module
 * 
 * Handles all recipe generation and modification logic including studio generation.
 * Extracted from recipeService.ts for better modularity.
 */

import { RecipeFormData, RecipeSource } from '../../types/recipe';
import { AssistantResponse } from '@/types/assistantTypes';
import { PROMPT_TEMPLATES, buildRecipeModificationPrompt } from '@/prompts/recipePrompts';
import {
    generateRecipe,
    generateRecipeModification,
    generateStudioContent
} from '../ai/aiGateway';
import { modelService } from '../modelService';
import { parseMarkdownToRecipeForm, parseModifiedRecipeResponse, parseRecipeFromJson, tryParseJsonFromText, extractChangesSummary, extractStructuredAppendix } from './recipeParsing';
import { RECIPE_JSON_RESPONSE_FORMAT } from '../ai/recipeSchema';
import { isValidPersonaId, loadPersonaGenerationPrompt, loadOutputContract } from '@/services/studioChat/personaLoader';
import { loadDietaryGuidance } from '@/services/dietaryService';

/**
 * Creates a structured recipe from raw text input using AI.
 *
 * Primary path: Gemini JSON mode (responseSchema) → parseRecipeFromJson (no regex).
 * Fallback path: markdown parsing (parseMarkdownToRecipeForm + raw-text fallback)
 * used when JSON mode fails (unsupported model, network error, etc.) or when the
 * model returned markdown despite the schema request.
 */
export async function handleRecipeCreationRequest(
    rawText: string,
    model?: string,
    metadata?: { persona?: string; dietaryTags?: string[] }
): Promise<AssistantResponse> {
    if (!rawText || rawText.trim().length < 10) {
        return { success: false, error: "Please provide more recipe content to work with." };
    }


    // Get model from service if not provided
    void (model || await modelService.getModelForFeature('textGeneration' as any));

    // Use centralized prompt template
    const systemPrompt = PROMPT_TEMPLATES.RECIPE_PARSING(rawText);

    // Use AI Gateway for proper provider routing — request structured JSON output
    // so we can skip the regex parser when the model supports it.
    const result = await generateRecipe(systemPrompt, 'handleRecipeCreationRequest', {
        responseFormat: RECIPE_JSON_RESPONSE_FORMAT,
    });

    if (result.success && result.text) {

        let formData: Partial<RecipeFormData>;
        let structuredMarkdown: string;

        // Try JSON first. If the model returned valid JSON, map it directly to form data
        // (clean, no truncation, no regex brittleness).
        const json = tryParseJsonFromText(result.text);
        if (json && typeof json === 'object') {
            formData = parseRecipeFromJson(json, metadata);
            // For backward compat with downstream consumers expecting a markdown string,
            // also serialize the JSON back to a markdown-ish representation.
            structuredMarkdown = result.text;
        } else {
            // Fallback: the model returned markdown (or JSON was malformed). Use the
            // legacy regex-based parser with the raw-text fallback for robustness.
            formData = parseMarkdownToRecipeForm(result.text, metadata, rawText);
            structuredMarkdown = result.text;
        }
        formData.source = RecipeSource.Assistant; // Set source for assistant-created recipes

        // POST-PROCESSING: Generate description if missing or empty
        if (!formData.description || formData.description.trim().length === 0) {

            const recipeContentForDesc = `# ${formData.title || 'Recipe'}\n\n${formData.ingredients || ''}\n\n${formData.method || ''}`;
            const descriptionPrompt = PROMPT_TEMPLATES.DESCRIPTION_GENERATION(recipeContentForDesc);
            const descriptionResult = await generateRecipe(descriptionPrompt, 'generateMissingDescription');

            if (descriptionResult.success && descriptionResult.text) {
                formData.description = descriptionResult.text.trim();
            }
        }

        return {
            success: true,
            message: "Recipe structured successfully",
            data: {
                structuredMarkdown,
                formData: formData
            }
        };
    } else {
        return {
            success: false,
            error: result.error || "Failed to structure recipe from raw text in assistantService.",
            data: result.debugInfo,
        };
    }
}

/**
 * Handles a request to modify an existing recipe using AI.
 *
 * @param personaId Optional studio persona — when provided (studio chat flow),
 *   the modification prompt carries the chef's chat profile so the rewritten
 *   recipe keeps the persona's voice (P1.7).
 */
export async function handleRecipeModificationRequest(
    currentRecipeData: RecipeFormData,
    modificationRequest: string,
    _model?: string,
    personaId?: string
): Promise<AssistantResponse> {

    if (!modificationRequest || modificationRequest.trim() === "") {
        return { success: false, error: "Modification request cannot be empty." };
    }

    try {
        // STAGE 1: Modify the recipe
        const modificationPrompt = buildRecipeModificationPrompt(currentRecipeData, modificationRequest, personaId);
        // Use AI Gateway for proper provider routing — request structured JSON output
        const modificationResult = await generateRecipeModification(
            modificationPrompt,
            'handleRecipeModificationRequest',
            { responseFormat: RECIPE_JSON_RESPONSE_FORMAT }
        );

        if (!modificationResult.success || !modificationResult.text) {
            return {
                success: false,
                error: modificationResult.error || "Failed to modify recipe via AI.",
                data: modificationResult.debugInfo,
            };
        }

        // Try JSON first (no regex). For modifications, overlay parsed fields onto
        // the existing recipe so any fields the AI didn't return are preserved.
        let updatedFormData: RecipeFormData;
        const json = tryParseJsonFromText(modificationResult.text);
        if (json && typeof json === 'object') {
            const parsed = parseRecipeFromJson(json);
            updatedFormData = {
                ...currentRecipeData,
                ...parsed,
            };
        } else {
            updatedFormData = parseModifiedRecipeResponse(modificationResult.text, currentRecipeData);
        }
        const changesSummary = extractChangesSummary(modificationResult.text);

        // STAGE 2: Verify and regenerate description if necessary
        const isDescriptionMissing = !updatedFormData.description;
        const isDescriptionStale = updatedFormData.description === currentRecipeData.description;

        if (isDescriptionMissing || isDescriptionStale) {

            const recipeContentForDesc = `# ${updatedFormData.title}\n\n${updatedFormData.ingredients}\n\n${updatedFormData.method}`;
            const descriptionPrompt = PROMPT_TEMPLATES.DESCRIPTION_GENERATION(recipeContentForDesc);
            // Use AI Gateway for proper provider routing
            const descriptionResult = await generateRecipeModification(descriptionPrompt, 'regenerateDescription');

            if (descriptionResult.success && descriptionResult.text) {
                updatedFormData.description = descriptionResult.text.trim();
            }
        }

        return {
            success: true,
            message: "Recipe modified successfully by AI." + (changesSummary ? ` Summary: ${changesSummary}` : ""),
            data: {
                formData: updatedFormData,
                structuredMarkdown: modificationResult.text,
                changesSummary: changesSummary
            }
        };

    } catch (error: any) {
        return {
            success: false,
            error: error.message || "An unexpected error occurred during recipe modification.",
        };
    }
}

/**
 * Maps studio dietary tag IDs to human-readable labels for prompt clarity.
 * Allergen tags carry an explicit strictness note — they are safety-level
 * restrictions, not preferences.
 */
const DIETARY_TAG_LABELS: Record<string, string> = {
    LowCarb: 'Low-Carb',
    HealthyLight: 'Healthy & Light',
    GlucoseFriendly: 'Glucose-Friendly',
    Vegetarian: 'Vegetarian',
    Vegan: 'Vegan',
    GlutenFree: 'Gluten-Free',
    DairyFree: 'Dairy-Free',
    NutFree: 'Nut-Free (strict allergy-level: no tree nuts, peanuts, nut oils, or nut flours)',
    ShellfishFree: 'Shellfish-Free (strict allergy-level: no crustaceans or molluscs, no oyster sauce or shellfish-derived stock)',
};

/**
 * Handles recipe generation from the Recipe Creation Studio.
 * Constructs a prompt, calls AI, parses response, and prepares data for RecipeForm.
 *
 * NOTE: This is the largest and most complex function - moved here in its entirety.
 */
export async function handleStudioRecipeGeneration(
    options: any & { model?: string; thinkingLevel?: 'MINIMAL' | 'LOW' | 'MEDIUM' | 'HIGH' }
): Promise<AssistantResponse> {
    const {
        persona,
        complexity,
        dietaryTags,
        specialRequest,
        coreRecipeQuery,
        language,
        languageCustom,
        thinkingLevel
    } = options;

    // Load actual persona content with measurement formatting requirements
    let personaPromptContent = `You are a helpful AI assistant that generates recipes.
The user wants a recipe for {coreRecipeQuery}.
Please provide a well-structured recipe in Markdown format with ALL sections:

# [Recipe Title - Keep it short, appetizing, and chef-inspired. DO NOT include dietary restrictions in the title (like "Low-Carb" or "Dairy-Free") as these will be noted separately]

## Description
[One concise sentence describing the dish]

## Ingredients
[List all ingredients as unordered list with bullets (-)]
- **CRITICAL REQUIREMENT:** List ingredients in the order they are used - always use metric first and imperial in (parentheses).
- **MEASUREMENT FORMAT:** Use format like "450g (1lb) ground turkey" or "240ml (1 cup) milk"
- Always round metric measurements when it makes sense to do so. For smaller measurements like tablespoons and teaspoons be cautious with rounding.

## Method
[List all instructions as ordered list with numbers (1.)]

## Notes
[Include helpful tips, storage suggestions, variations, or chef's notes that enhance the recipe. This section should provide value beyond the basic recipe.]

Consider any specific requests or modifications provided by the user.`;

    // Load persona content — generation portion only (everything below the
    // CHAT_PROFILE_END marker), so the chat profile never leaks into
    // recipe-generation prompts. Then append the P1.4 shared output contract
    // (format, measurements, language, anti-slop, structured appendix) so those
    // rules are stated ONCE rather than duplicated inside every persona file.
    try {
        if (persona && isValidPersonaId(persona)) {
            personaPromptContent = `${loadPersonaGenerationPrompt(persona)}\n\n---\n\n${loadOutputContract()}`;
        }
    } catch {
    }


    const modifications = [];
    if (complexity && complexity !== 'Standard') {
        modifications.push(`- **Complexity Level:** ${complexity}.`);
        if (complexity === 'Simple') {
            modifications.push("  You should actively look for smart shortcuts that don't compromise the final dish too much, such as using high-quality store-bought pasta or sauce.");
        } else if (complexity === 'Ambitious') {
            modifications.push("  You should suggest extra steps or techniques for a more refined result, like making components from scratch if applicable.");
        }
    }
    let dietaryGuidance: string | null = null;
    if (dietaryTags && dietaryTags.length > 0) {
        const dietaryLabels = dietaryTags.map((tag: string) => DIETARY_TAG_LABELS[tag] || tag);
        modifications.push(`- **Dietary Focus:** ${dietaryLabels.join(', ')}. The entire recipe must adhere to these dietary requirements.`);
        modifications.push(`- **Measurement Format:** Remember to use metric first with imperial in parentheses for ALL ingredients, even when making dietary substitutions.`);

        // P1.6: inject the authoritative dietary guidance docs for the selected tags
        try {
            dietaryGuidance = await loadDietaryGuidance(dietaryTags);
        } catch {
        }
    }
    if (specialRequest && specialRequest.trim() !== '') {
        modifications.push(`- **User's Special Request:** "${specialRequest.trim()}"`);
    }

    // Language handling precedence: explicit selection overrides regex detection
    let languageOverride: string | null = null;

    // 1) Explicit UI selection
    const explicit = (languageCustom && languageCustom.trim().length > 0)
        ? languageCustom.trim()
        : (language && language !== "English (UK)" ? language : "");

    if (explicit) {
        languageOverride = explicit;
    } else {
        // 2) Fallback to previous regex detection from Special Request
        if (specialRequest && specialRequest.trim() !== '') {
            const languageRegex = /(?:in|write in|output in|use|entire recipe in|respond in|translate to|translate into|return in|produce in)\s+(french|spanish|german|italian|chinese|japanese|portuguese|russian|arabic|hindi|korean|turkish|greek|dutch|swedish|norwegian|danish|finnish|polish|czech|hebrew|thai|vietnamese|indonesian|malay|romanian|bulgarian|ukrainian|hungarian|slovak|croatian|serbian|slovenian|estonian|latvian|lithuanian|filipino|persian|urdu|swahili|afrikaans|icelandic|irish|welsh|scots|gaelic|catalan|galician|basque|esperanto|latin)\b/i;
            const match = specialRequest.match(languageRegex);
            if (match) {
                languageOverride = match[1][0].toUpperCase() + match[1].slice(1).toLowerCase();
            }
        }
    }

    let systemOverride = `SYSTEM: The recipe description must be a single, concise sentence summarizing the dish. The recipe title must be short and appetizing without dietary restrictions mentioned (those will be in the notes). Do not write a story or use flowery language. \n\nCRITICAL MEASUREMENT REQUIREMENT: All ingredient measurements MUST use metric first with imperial in parentheses. Format: "450g (1lb) ground turkey" or "240ml (1 cup) milk". This is mandatory for all recipes.\n\nOUTPUT CONTRACT: Your response has exactly TWO parts, both required: (1) the full markdown recipe following your methodology, then (2) IMMEDIATELY after the final recipe section, a fenced \`recipe-data\` code block containing the structured JSON specified in the **Shared Output Contract** below. Never end your response after the markdown recipe without the recipe-data block.\n\nAll recipe content must use UK English by default, unless the user has specifically requested another language.`;
    if (languageOverride) {
        systemOverride += `

OUTPUT LANGUAGE REQUIREMENT:
- Output language: [${languageOverride}]
- You must write the entire recipe (title, description, ingredients, method, notes) exclusively in the output language specified above.
- Do not use other languages in any section. If measurement units require Latin characters, keep only the units in their conventional form.
- This instruction takes precedence over persona tone and any other earlier or later instructions.`;
    }
    systemOverride += `

This overrides any other instructions.`;

    let finalPrompt = `${systemOverride}\n\n${personaPromptContent}`.replace('{coreRecipeQuery}', coreRecipeQuery);

    if (!personaPromptContent.includes('{coreRecipeQuery}')) {
        finalPrompt += `\n\n---\n\nThe user wants a recipe for **${coreRecipeQuery}**.`;
    }

    if (modifications.length > 0) {
        finalPrompt += "\n\nPlease generate the recipe according to your core methodology (or the general instructions if using fallback), but apply the following specific modifications based on the user's final choices:\n\n";
        finalPrompt += modifications.join('\n');
    } else if (!personaPromptContent.includes('{coreRecipeQuery}')) {
        finalPrompt += "\n\nPlease generate the recipe according to your core methodology.";
    }

    if (dietaryGuidance) {
        finalPrompt += `\n\n---\n\n${dietaryGuidance}\n\nThe dietary guidance above is authoritative. Where it conflicts with the persona's default ingredient choices or the user's other preferences, the dietary guidance wins.`;
    }

    finalPrompt += `\n\n---\n**FINAL REMINDER:** Follow the **Shared Output Contract** above exactly — including the mandatory \`recipe-data\` appendix after the Notes section. The response is incomplete without it. This is a critical instruction.`;

    // Minimal normalization to prevent double-bullets before parsing:
    // - Convert common typographic bullets to markdown "- "
    // - Avoid touching ordered lists (1. ), headings, or code blocks
    const normalizeBullets = (text: string): string => {
        try {
            // Replace leading typographic bullets with markdown dash
            let t = text.replace(/^(\s*)[•·◦]\s+/gm, '$1- ');
            // Replace doubled marker cases like "- • " at start of line with single "- "
            t = t.replace(/^(\s*)-\s*[•·◦]+\s+/gm, '$1- ');
            // Ensure ingredients bullets are single dashes; do not alter ordered lists
            return t;
        } catch {
            return text;
        }
    };

    // Use AI Gateway for proper provider routing (with thinking level if provided)
    // Request structured JSON output so we can skip the regex parser when supported.
    const aiResult = await generateStudioContent(
        finalPrompt,
        'handleStudioRecipeGeneration',
        thinkingLevel,
        { responseFormat: RECIPE_JSON_RESPONSE_FORMAT }
    );

    if (aiResult.success && aiResult.text) {
        // P1.2: extract the structured appendix FIRST so the JSON block never
        // pollutes the markdown sections (especially notes), then parse the
        // remaining markdown as before.
        const { structured, markdown: markdownWithoutAppendix } = extractStructuredAppendix(aiResult.text);
        const cleanedForParse = normalizeBullets(markdownWithoutAppendix);

        // Try JSON first (no regex), fall back to markdown parser. Guarded:
        // markdown prose must not be mistaken for a JSON recipe — only take
        // the JSON path when the object carries string recipe fields.
        let formData: Partial<RecipeFormData>;
        const json = tryParseJsonFromText(cleanedForParse);
        const jsonLooksLikeRecipe = !!(json && typeof json === 'object' &&
            (typeof (json as Record<string, unknown>).ingredients === 'string' ||
             typeof (json as Record<string, unknown>).title === 'string'));
        if (jsonLooksLikeRecipe) {
            formData = parseRecipeFromJson(json, { persona, dietaryTags });
        } else {
            formData = parseMarkdownToRecipeForm(cleanedForParse, {
                persona,
                dietaryTags
            });
        }
        // Attach the structured layer whichever parser ran (the JSON path may
        // already have populated it from obj.structured).
        if (structured && !formData.structured) {
            formData.structured = structured;
        }

        const creationResult: AssistantResponse = {
            success: true,
            message: "Recipe structured successfully",
            data: {
                structuredMarkdown: cleanedForParse,
                formData: formData
            }
        };

        if (creationResult.success && creationResult.data) {
            creationResult.data.formData.fromStudio = true;
            creationResult.data.formData.studioPersona = persona;
            creationResult.data.formData.source = RecipeSource.Studio;

            // The home page card components (StudioRecipeCard, ModernRecipeCard)
            // look up the chef via recipe.tags, NOT via studioPersona. Mirror the
            // persona ID into tags so the chef avatar renders on the home page.
            // Without this, newly created studio recipes show a generic badge
            // instead of the chef's avatar.
            if (persona) {
                const currentTags = creationResult.data.formData.tags || [];
                if (!currentTags.includes(persona)) {
                    creationResult.data.formData.tags = [...currentTags, persona];
                }
            }

            // NEW: Trigger nutritional analysis using the same model
            if (creationResult.data.formData.ingredients) {
                // We can call this and not wait for it (fire and forget) or await it if we need the data
                // analyzeDietaryCompliance(creationResult.data.formData.ingredients, modelToUse);
            }
        }
        return creationResult;
    } else {
        return {
            success: false,
            error: aiResult.error || "Failed to generate recipe from studio via AI.",
            data: aiResult.debugInfo,
        };
    }
}
